import React from 'react';
import './ClientSearchHeader.css';
import ManagementFilterSkeleton from '../../../shared/management-layout/ManagementFilterSkeleton';
import { SkeletonBlock } from '../../loading/skeletons';

const IconPlus = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const IconMail = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const IconImport = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const IconExport = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const IconRefresh = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

const IconSearch = () => (
  <svg className="clientSearchInputIcon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);

const IconGallery = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
  </svg>
);

const IconTable = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <line x1="8" y1="6" x2="21" y2="6" />
    <line x1="8" y1="12" x2="21" y2="12" />
    <line x1="8" y1="18" x2="21" y2="18" />
    <line x1="3" y1="6" x2="3.01" y2="6" />
    <line x1="3" y1="12" x2="3.01" y2="12" />
    <line x1="3" y1="18" x2="3.01" y2="18" />
  </svg>
);

const ClientSearchHeader = ({
  searchTerm,
  onSearchChange,
  onAddClient,
  canCreateClient,
  onInviteClient,
  onRefresh,
  isRefreshing,
  viewMode,
  onViewModeChange,
  listMode = 'active',
  onListModeChange,
  totalClients = 0,
  newClientsThisMonth = 0,
  onImportClick,
  onExportClick,
  isLoading = false,
}) => {
  const isArchivedView = listMode === 'archived';

  return (
    <div className="app-table-wrap clientSearchHeader">
      <header className="clientSearchHeaderBar" aria-label="Client list">
        <div className="clientSearchHeaderMeta">
          {isLoading ? (
            <SkeletonBlock variant="pill" width={88} height={22} />
          ) : (
            <>
              <span className="clientSearchHeaderPill">
                {totalClients} {isArchivedView ? 'archived' : totalClients === 1 ? 'client' : 'clients'}
              </span>
              {!isArchivedView && newClientsThisMonth > 0 ? (
                <span className="clientSearchHeaderPill clientSearchHeaderPill--new">
                  {newClientsThisMonth} new this month
                </span>
              ) : null}
            </>
          )}
        </div>
      </header>

      <div className="clientSearchHeaderBody">
        {isLoading ? (
          <ManagementFilterSkeleton variant="client" label="Loading client filters" />
        ) : (
        <>
        <div className="clientSearchHeaderToolbar">
          <div className="managementFilterSection">
            <p className="managementFilterSectionLabel">Status</p>
            <div className="clientSearchHeaderListMode" role="tablist" aria-label="Client list">
              <button
                type="button"
                role="tab"
                aria-selected={listMode === 'active'}
                className={`clientSearchHeaderListModeBtn${listMode === 'active' ? ' is-active' : ''}`}
                onClick={() => onListModeChange?.('active')}
              >
                Active
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={listMode === 'archived'}
                className={`clientSearchHeaderListModeBtn${listMode === 'archived' ? ' is-active' : ''}`}
                onClick={() => onListModeChange?.('archived')}
              >
                Archived
              </button>
            </div>
          </div>

          <div className="managementFilterSection">
            <p className="managementFilterSectionLabel">Search</p>
            <div className="clientSearchInputWrapper">
              <IconSearch />
              <input
                className="clientSearchInput"
                type="search"
                placeholder={isArchivedView ? 'Search archived clients…' : 'Search by name or email…'}
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                aria-label={isArchivedView ? 'Search archived clients' : 'Search clients'}
              />
            </div>
          </div>

          <div className="managementFilterSection">
            <p className="managementFilterSectionLabel">View</p>
            <div className="clientSearchViewToggle" role="group" aria-label="View mode">
              <button
                type="button"
                className={`clientSearchViewToggleBtn ${viewMode === 'gallery' ? 'active' : ''}`}
                onClick={() => onViewModeChange('gallery')}
                title="Gallery view"
                aria-pressed={viewMode === 'gallery'}
              >
                <IconGallery />
                <span className="clientSearchViewToggleLabel">Gallery</span>
              </button>
              <button
                type="button"
                className={`clientSearchViewToggleBtn ${viewMode === 'table' ? 'active' : ''}`}
                onClick={() => onViewModeChange('table')}
                title="Table view"
                aria-pressed={viewMode === 'table'}
              >
                <IconTable />
                <span className="clientSearchViewToggleLabel">Table</span>
              </button>
            </div>
          </div>
        </div>

        <div className="managementFilterSection">
          <p className="managementFilterSectionLabel">Actions</p>
          <div className="clientSearchHeaderActions">
          {!isArchivedView ? (
            <>
              <button
                type="button"
                className="clientSearchHeaderBtn"
                onClick={onAddClient}
                disabled={!canCreateClient}
              >
                <IconPlus />
                Add Client
              </button>
              {onInviteClient ? (
                <button
                  type="button"
                  className="clientSearchHeaderBtn secondary"
                  onClick={onInviteClient}
                  disabled={!canCreateClient}
                >
                  <IconMail />
                  Invite
                </button>
              ) : null}
              <span className="clientSearchHeaderActionsSpacer" />
              <button type="button" className="clientSearchHeaderBtn ghost" onClick={onImportClick}>
                <IconImport />
                Import
              </button>
              <button type="button" className="clientSearchHeaderBtn ghost" onClick={onExportClick}>
                <IconExport />
                Export
              </button>
            </>
          ) : null}
          <button
            type="button"
            className={`clientSearchHeaderBtn ghost refresh ${isRefreshing ? 'refreshing' : ''}`}
            onClick={onRefresh}
            title="Refresh"
            aria-label="Refresh clients"
          >
            <IconRefresh />
            Refresh
          </button>
          </div>
        </div>
        </>
        )}
      </div>
    </div>
  );
};

export default ClientSearchHeader;
